import { useSelector } from "react-redux";

import { BASE_64_1X1_GREY, PRODUCT_TYPE_IDS } from "@chef/constants";
import {
  IDeviationBasket,
  selectDeviationProducts,
  usePickAndMixQuery,
} from "@chef/state-management";
import { getRecipeFromPickAndMix } from "@chef/state-management/helpers";
import {
  useExtendedBasketData,
  useUpdateBasketPickAndMixDeviation,
} from "@chef/state-management/hooks";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { sumByProperty } from "@chef/utils/array";

import { Meals } from "../shared/Meals";
import { useFinancesForDeviation } from "./useFinancesForDeviation";

interface DeviationMealsProps {
  deviation: IDeviationBasket;
  initiallyExpanded?: boolean;
  allowMealRemoval?: boolean;
  showImages?: boolean;
  minMeals: number;
}

export const DeviationMeals = ({
  deviation,
  initiallyExpanded,
  allowMealRemoval,
  showImages,
  minMeals,
}: DeviationMealsProps) => {
  const { week, year } = deviation;

  const { updateBasketPickAndMixDeviation } =
    useUpdateBasketPickAndMixDeviation({ week, year });

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const { data: pickAndMixQuery } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const { data: extendedBasketData } = useExtendedBasketData({ week, year });

  const { finances } = useFinancesForDeviation({
    deviation,
  });

  if (!pickAndMixQuery) {
    return null;
  }

  const pickAndMix = pickAndMixQuery.pickAndMix;

  const selectedPickAndMixWithRecipe = deviation.products
    .filter(
      ({ productTypeId }) => productTypeId === PRODUCT_TYPE_IDS.PICKANDMIX,
    )
    .map((product) => ({
      ...product,
      recipe: getRecipeFromPickAndMix({
        pickAndMix,
        productId: product.productId,
        variationId: product.variationId,
      }),
    }));

  if (
    !selectedPickAndMixWithRecipe ||
    !extendedBasketData ||
    !selectedProducts
  ) {
    console.warn(
      "DeviationMeals rendering without required data. This is not necessarily an error, but it could mean that some required pre-loading is not happening, which could cause UI glitches.",
      { selectDeviationProducts, extendedBasketData, selectedProducts },
    );
    return null;
  }

  const portions = extendedBasketData.portions;

  const extraPrice = sumByProperty(selectedPickAndMixWithRecipe, "price");

  return (
    <Meals
      dishes={selectedPickAndMixWithRecipe.map(
        ({ recipe, price, productId, variationId }) => {
          const url =
            (!!recipe && findFeaturedPortraitImage(recipe)?.url) ||
            BASE_64_1X1_GREY;

          return {
            name: recipe?.recipeName,
            extraPrice: price,
            imageUrl: showImages ? url : undefined,
            onRemove: allowMealRemoval
              ? () => {
                  if (!recipe) {
                    return;
                  }

                  updateBasketPickAndMixDeviation({
                    basketProducts: selectedProducts,
                    portions,
                    updateProduct: {
                      productId,
                      variationId,
                      mainRecipeId: recipe.mainRecipeId,
                      recipeId: recipe.recipeId,
                      recipeName: recipe.recipeName,
                      price,
                    },
                  });
                }
              : undefined,
          };
        },
      )}
      portions={portions}
      priceOfMeals={finances.mealbox + extraPrice}
      initiallyExpanded={initiallyExpanded}
      minMeals={minMeals}
    />
  );
};
